import { agentHooks } from '../hooks';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../lib/locale';

interface AgentPerformanceTabProps {
  agentId: string;
}

export function AgentPerformanceTab({ agentId }: AgentPerformanceTabProps) {
  const { data: response, isLoading } = agentHooks.usePerformance(agentId);

  if (isLoading) {
    return <div className="py-8 text-center text-text-muted">Loading performance data...</div>;
  }

  const data = response?.data || [];

  if (data.length === 0) {
    return (
      <div className="py-12 text-center text-text-muted border border-border rounded-md bg-surface-1">
        No performance history available for this agent yet.
      </div>
    );
  }

  const latestStats = data[data.length - 1]; // Assuming sorted by period Month ascending

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(DEFAULT_LOCALE, { style: 'currency', currency: DEFAULT_CURRENCY, maximumFractionDigits: 0 }).format(value / 100);
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-surface-1 border border-border p-4 rounded-md flex flex-col gap-1">
          <span className="text-sm text-text-secondary">This Month Collections</span>
          <span className="text-2xl font-semibold">{formatCurrency(latestStats.collectionsAmount)}</span>
          <span className="text-xs text-text-muted">{latestStats.collectionsCount} receipts</span>
        </div>
        <div className="bg-surface-1 border border-border p-4 rounded-md flex flex-col gap-1">
          <span className="text-sm text-text-secondary">New Customers</span>
          <span className="text-2xl font-semibold">{latestStats.newCustomers}</span>
          <span className="text-xs text-text-muted">Onboarded this month</span>
        </div>
        <div className="bg-surface-1 border border-border p-4 rounded-md flex flex-col gap-1">
          <span className="text-sm text-text-secondary">Territory Defaults</span>
          <span className="text-2xl font-semibold text-danger-500">{latestStats.defaultsInTerritory}</span>
          <span className="text-xs text-text-muted">Active defaults in primary territory</span>
        </div>
      </div>

      <div className="bg-surface-1 border border-border p-6 rounded-md">
        <h3 className="text-lg font-medium text-text-primary mb-6">Collection History</h3>
        <div className="h-80 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
              <XAxis dataKey="periodMonth" stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis yAxisId="left" orientation="left" stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `${value / 100}`} />
              <YAxis yAxisId="right" orientation="right" stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} />
              <Tooltip 
                formatter={(value: any, name: any) => {
                  if (name === 'Amount') return [formatCurrency(value as number), name];
                  return [value, name];
                }}
                contentStyle={{ backgroundColor: 'var(--surface-1)', borderColor: 'var(--border)', borderRadius: '6px' }}
              />
              <Legend />
              <Bar yAxisId="left" dataKey="collectionsAmount" name="Amount" fill="var(--accent-500)" radius={[4, 4, 0, 0]} maxBarSize={50} />
              <Bar yAxisId="right" dataKey="collectionsCount" name="Receipt Count" fill="var(--surface-3)" radius={[4, 4, 0, 0]} maxBarSize={50} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
